// My Dashboard (PlunkDocs specs/MY_DASHBOARD_2026-10-22.md): the Plunk card catalog, Plunk's default layout, the
// layout API (AnalyticsService /v1/dashboard) and report charts (step 2). A layout is an ordered list of cards:
// {id, source: "plunk", key, size} or {id, source: "report", reportId, panelId, size}, size 4|6|8|12. A report chart is
// pinned by reference: edits to the report show up on every Dashboard that pins it. Every card follows the global date
// picker and its comparison, the app-wide standard (DS 2026-10-22): no card keeps dates of its own. Which layout shows, first match wins: the person's own → the workspace default → Plunk's default.
// Cards the workspace can't show are skipped (kept in the saved list, so a feature switched off and on again
// brings them back). A layout never grants access.
import ServiceFactory from "../../service";
import { hasTenantFeature } from "../../functions/tenant-features";
import { hasWidgets } from "../../components/tenant-modules";
import { runQs } from "../reports/common";
import account from "../../data/account";
export const MAX_CARDS = 24;
export const SIZE_LABEL = {
  4: "⅓",
  6: "½",
  8: "⅔",
  12: "Full"
};
const ALL = [4, 6, 8, 12];

/** The built-in cards. `sizes` = what the card can be (ribbons and notices are full width only). */
export const CATALOG = [{
  key: "changed",
  label: "What changed",
  desc: "The biggest movers in plain words",
  icon: "bolt",
  sizes: [12]
}, {
  key: "sales",
  label: "Sales",
  desc: "Revenue, year to date, orders and buying accounts",
  icon: "payments",
  sizes: [12],
  gate: c => c.hasSales
}, {
  key: "activity",
  label: "Site activity",
  desc: "Impressions, visitors, conversions and product interactions",
  icon: "insights",
  sizes: [12]
}, {
  key: "funnel",
  label: "Funnel",
  desc: "Leads, prospects and new customers this period",
  icon: "filter_alt",
  sizes: ALL,
  gate: c => c.hasLifecycle
}, {
  key: "trend",
  label: "Revenue trend",
  desc: "Trailing 12 months, monthly",
  icon: "bar_chart",
  sizes: ALL,
  gate: c => c.hasSales
}, {
  key: "conversions",
  label: "Conversions",
  desc: "By type, this period",
  icon: "how_to_reg",
  sizes: ALL
}, {
  key: "interactions",
  label: "Site interactions",
  desc: "Search, locator, cart and studio adds",
  icon: "touch_app",
  sizes: ALL
}, {
  key: "products",
  label: "Top products",
  desc: "Most-viewed products this period",
  icon: "chair",
  sizes: ALL
}, {
  key: "modules",
  label: "Workspace modules",
  desc: "Widgets set up for this workspace",
  icon: "widgets",
  sizes: [12],
  gate: c => c.hasModules
}];
export const entryOf = key => CATALOG.find(e => e.key === key) || null;
export function workspaceContext() {
  return {
    hasSales: hasTenantFeature("salesTransactions"),
    hasLifecycle: hasTenantFeature("lifecycle"),
    hasModules: hasWidgets("dashboard")
  };
}

/** Whether this workspace can show the card. Report charts are always drawn; whether the person can see the report is
 *  the panel run's answer (not_shared, panel_removed, report_removed become card states). */
export function available(card, ctx) {
  if (card && card.source === "report") return !!(card.reportId && card.panelId);
  if (!card || card.source !== "plunk") return false;
  const e = entryOf(card.key);
  return !!e && (!e.gate || e.gate(ctx));
}

/** The size a card is drawn at: its saved size when the card allows it, else the nearest it does. */
export function sizeOf(card) {
  if (card.source === "report") return ALL.includes(card.size) ? card.size : 6;
  const e = entryOf(card.key);
  const sizes = e && e.sizes || ALL;
  return sizes.includes(card.size) ? card.size : sizes[sizes.length - 1];
}
export const plunkCard = (key, size) => ({
  id: `p-${key}`,
  source: "plunk",
  key,
  size: size || (entryOf(key) || {}).sizes.slice(-1)[0] || 12
});

/** Plunk's default: today's Dashboard, built from what the workspace has. */
export function plunkDefault(ctx) {
  const c = ctx || workspaceContext();
  return [plunkCard("changed"), c.hasSales && plunkCard("sales"), plunkCard("activity"), c.hasLifecycle && plunkCard("funnel", c.hasSales ? 4 : 12), c.hasSales && plunkCard("trend", c.hasLifecycle ? 8 : 12), plunkCard("conversions", 6), plunkCard("interactions", 6), plunkCard("products", 12), c.hasModules && plunkCard("modules")].filter(Boolean);
}
const svc = api => {
  const S = ServiceFactory.getType(api, "product");
  return new S();
};
const strip = cards => (cards || []).map(c => c.source === "report" ? {
  id: c.id,
  source: "report",
  reportId: c.reportId,
  panelId: c.panelId,
  size: c.size
} : {
  id: c.id,
  source: "plunk",
  key: c.key,
  size: c.size
});

/** { source: mine|default|plunk, cards, defaultChanged, defaultBy, hasDefault, canSetDefault } with cards always filled. */
export async function getMyLayout() {
  const r = (await svc("/dashboard/layout/me").getAsync()) || {};
  const cards = Array.isArray(r.cards) ? r.cards : plunkDefault(); // an empty saved layout stays empty (they removed everything)
  return {
    ...r,
    source: Array.isArray(r.cards) ? r.source : "plunk",
    cards
  };
}
export const saveMyLayout = cards => svc("/dashboard/layout/me").putAsync({
  cards: strip(cards)
});
export const resetMyLayout = () => svc("/dashboard/layout").deleteAsync("me");
export const seenDefault = () => svc("/dashboard/layout/me/seen-default").postAsync({});
export const getDefaultLayout = () => svc("/dashboard/layout/default").getAsync();
export const saveDefaultLayout = cards => svc("/dashboard/layout/default").putAsync({
  cards: strip(cards)
});
export const clearDefaultLayout = () => svc("/dashboard/layout").deleteAsync("default");

// ---- report charts (step 2)
export const reportCardId = (reportId, panelId) => `r-${reportId}-${panelId}`;
/** A report panel's saved width as a Dashboard size: third ⅓, half ½, else full. */
export const sizeFromWidth = w => w === "third" ? 4 : w === "half" ? 6 : 12;
export const reportCard = (reportId, panelId, width) => ({
  id: reportCardId(reportId, panelId),
  source: "report",
  reportId,
  panelId,
  size: sizeFromWidth(width)
});

/** The global comparison (top bar) in the reports' vocabulary: prev_period → previous_period, prev_year → previous_year. */
export function reportComparison(comp) {
  const s = String(comp || "");
  if (s === "prev_year" || s === "previous_year") return "previous_year";
  if (s === "none") return "none";
  return "previous_period";
}
export const isPinned = (cards, reportId, panelId) => (cards || []).some(c => c.source === "report" && c.reportId === reportId && c.panelId === panelId);

/** Every panel of every report the person can see: [{id, name, createdBy, shared, mine, range, panels:[{id, kind, title, dataset, width}]}]. */
export async function listReportPanels() {
  const r = await svc("/reports/panels").getAsync();
  return r && r.items || [];
}
const PANEL_ERRORS = {
  report_removed: "This report was deleted.",
  not_shared: "This report is no longer shared with you.",
  panel_removed: "This chart was removed from its report.",
  failed: "This chart could not load."
};

/** One panel's run ({range, comparison, window, panels:[result]}) on the global range and comparison.
 *  Rejects with {code, error}: not_shared | panel_removed | report_removed | failed. */
export async function runReportPanel(reportId, panelId, range, comparison) {
  const qs = [range ? runQs(range) : "", comparison ? `comparison=${encodeURIComponent(comparison)}` : ""].filter(Boolean).join("&");
  const q = qs ? `?${qs}` : "";
  try {
    return await svc(`/reports/${encodeURIComponent(reportId)}/panels/${encodeURIComponent(panelId)}/run${q}`).getAsync();
  } catch (e) {
    // The service layer keeps only `message` from an error body; the route puts its code there.
    const code = PANEL_ERRORS[e && e.message] ? e.message : "failed";
    throw {
      code,
      error: PANEL_ERRORS[code]
    };
  }
}

/** Pin report panels to the person's own Dashboard (their layout, or the one they see, plus these at the end).
 *  items = [{reportId, panelId, width}] → {added, already, full}. */
export async function pinPanels(items) {
  const layout = await getMyLayout();
  const cards = layout.cards.slice();
  let added = 0,
    already = 0,
    full = 0;
  items.forEach(it => {
    if (isPinned(cards, it.reportId, it.panelId)) {
      already++;
      return;
    }
    if (cards.length >= MAX_CARDS) {
      full++;
      return;
    }
    cards.push(reportCard(it.reportId, it.panelId, it.width));
    added++;
  });
  if (added) await saveMyLayout(cards);
  return {
    added,
    already,
    full,
    cards
  };
}

// ---- last-seen layout (per browser, per person and workspace): lets the Dashboard draw and start fetching at once
// instead of waiting for GET layout/me. A convenience only: the server's answer always replaces it.
function cacheKey() {
  try {
    const a = account.get() || {};
    const e = account.getEmail && account.getEmail() || a.email || "";
    return a.tenantId ? `plunk-dash-layout:${a.tenantId}:${String(e).toLowerCase()}` : null;
  } catch (e) {
    return null;
  }
}
export function cachedLayout() {
  const k = cacheKey();
  if (!k) return null;
  try {
    const v = JSON.parse(localStorage.getItem(k) || "null");
    return v && Array.isArray(v.cards) ? v : null;
  } catch (e) {
    return null;
  }
}
export function rememberLayout(layout) {
  const k = cacheKey();
  if (!k || !layout) return;
  try {
    localStorage.setItem(k, JSON.stringify({
      source: layout.source,
      cards: strip(layout.cards)
    }));
  } catch (e) {/* private mode, full storage */}
}

/** Same cards, order and sizes. */
export const sameLayout = (a, b) => JSON.stringify(strip(a)) === JSON.stringify(strip(b));